import { FC, useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Box, Typography, Divider, CircularProgress, Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../store/hooks';
import { fetchBusinessSummaryPreview, sendBusinessSummaryWhatsApp } from '../../store/accountThunk';
import Filters from '../Filters/Filters';
import { VocuherCategory } from '../../utils/common-utils';

interface Props {
  accountId: string;
}

const ACCENT = '#84cc16';
const TEXT = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#eef0f3';

const PreviewRow: FC<{ label: string; value: string; strong?: boolean; caption?: string }> = ({ label, value, strong, caption }) => (
  <Box sx={{ py: 0.35 }}>
    <Stack direction="row" justifyContent="space-between">
      <Typography sx={{ fontSize: '0.82rem', color: MUTED }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: strong ? 700 : 600, color: TEXT }}>{value}</Typography>
    </Stack>
    {caption && (
      <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.1 }}>{caption}</Typography>
    )}
  </Box>
);

const BusinessSummarySend: FC<Props> = ({ accountId }) => {
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);
  // Date range comes from the shared Filters component (same as the Balance Sheet report).
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // The preset name ("This Month", "Today", …). The backend appends the date range to it for {{2}}.
  const [periodName, setPeriodName] = useState('');
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Filters emits the query string "start=YYYY-MM-DD&end=YYYY-MM-DD&...". Parse out start/end.
  const handleFilter = useCallback((queryString: string) => {
    const params = new URLSearchParams(queryString);
    setStartDate(params.get('start') || '');
    setEndDate(params.get('end') || '');
  }, []);

  // Filters also exposes the selected preset name via dateRangeLabel.
  const handleSelectedFilters = useCallback((data: any) => {
    setPeriodName(data?.dateRangeLabel || '');
  }, []);

  const loadPreview = useCallback(async (start: string, end: string, name: string) => {
    if (!accountId || !start || !end) return;
    setLoading(true);
    setSummary(null);
    try {
      const res: any = await dispatch(fetchBusinessSummaryPreview({
        accountId,
        startDate: start,
        endDate: end,
        periodName: name,
      })).unwrap();
      setSummary(res?.summary || null);
    } catch (error) {
      console.error('Error loading business summary preview:', error);
      toast.error('Could not load the business summary preview.');
    } finally {
      setLoading(false);
    }
  }, [accountId, dispatch]);

  // Refresh the preview when the range changes while open. Debounced + deduped: the shared Filters
  // emits start/end as separate state updates (multiple re-renders), so without this it would fire
  // the same request several times. The timer collapses them into ONE call with the final state.
  const lastFetchKey = useRef('');
  useEffect(() => {
    if (!(open && startDate && endDate)) return;
    const key = `${startDate}|${endDate}|${periodName}`;
    const timer = setTimeout(() => {
      if (lastFetchKey.current === key) return; // same range already loaded — skip
      lastFetchKey.current = key;
      loadPreview(startDate, endDate, periodName);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startDate, endDate, periodName]);

  // Reset the dedupe key each time the dialog opens so it always loads a fresh preview.
  useEffect(() => {
    if (open) lastFetchKey.current = '';
  }, [open]);

  const handleSend = async () => {
    if (!accountId || sending || !startDate || !endDate) return;
    setSending(true);
    try {
      const res: any = await dispatch(sendBusinessSummaryWhatsApp({
        accountId,
        startDate,
        endDate,
        periodName,
      })).unwrap();
      toast.success(res?.message || 'Business summary sent on WhatsApp.');
    } catch (error: any) {
      const msg = error?.message || error?.response?.data?.message || 'Failed to send the business summary.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        startIcon={<AssessmentOutlinedIcon sx={{ color: '#fff !important' }} />}
        sx={{
          backgroundColor: '#059669',
          color: '#fff',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.82rem',
          borderRadius: 999,
          border: '1px solid #059669',
          boxShadow: 'none',
          px: 1.75,
          py: 0.6,
          '& .MuiButton-startIcon': { mr: 0.75 },
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { backgroundColor: '#047857', borderColor: '#047857', boxShadow: '0 6px 16px rgba(5,150,105,0.3)', transform: 'translateY(-2px)', color: '#fff' },
          '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 4px rgba(5,150,105,0.2)' }
        }}
      >
        Business Summary
      </Button>

      <Dialog open={open} onClose={(_e, reason) => {
        // Close only via the X / Cancel buttons — ignore backdrop click and Esc.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        if (!sending) setOpen(false);
      }} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '1rem' }}>Send Business Summary</Typography>
            <IconButton size="small" onClick={() => setOpen(false)} disabled={sending}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {/* Reusable date filter (same component as the Sales listing & Balance Sheet report).
              hideFilter + hidePartySearch leave only the date select + Custom Date range. */}
          <Box sx={{
            display: 'flow-root', mt: 2, mb: 1.25, minHeight: 48,
            // The shared Filters' date select has a hardcoded left margin; drop it here so it aligns
            // flush-left with the preview card below.
            '& .MuiOutlinedInput-root': { ml: '0 !important' },
          }}>
            <Filters
              type={VocuherCategory.SALE}
              filterHandler={handleFilter}
              selectedFilters={handleSelectedFilters}
              hideFilter={true}
              hidePartySearch={true}
              hideReset={true}
            />
          </Box>

          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, backgroundColor: '#fafdf4', minHeight: 430 }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 394 }}>
                <CircularProgress size={24} sx={{ color: ACCENT }} />
              </Box>
            ) : summary ? (
              <>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.9rem', mb: 0.5 }}>Business Summary</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.8rem' }}>Hello {summary.recipientName},</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.8rem', mb: 1 }}>
                  Your business summary for {summary.period}:
                </Typography>
                <PreviewRow
                  label="Total Sales (incl. GST)"
                  value={`₹${summary.totalSalesWithGst ?? summary.totalSales}`}
                  caption={summary.totalSalesWithGst && summary.totalSalesWithGst !== summary.totalSales
                    ? `Taxable ₹${summary.totalSales} + GST ₹${summary.gstOnSales}` : undefined}
                />
                <PreviewRow
                  label="Total Purchase (incl. GST)"
                  value={`₹${summary.totalPurchaseWithGst ?? summary.totalPurchase}`}
                  caption={summary.gstOnPurchase && summary.totalPurchaseWithGst !== summary.totalPurchase
                    ? `Taxable ₹${summary.totalPurchase} + GST ₹${summary.gstOnPurchase}` : undefined}
                />
                <PreviewRow label="Total Expenses" value={`₹${summary.totalExpenses}`} />
                <PreviewRow label="Net Profit" value={`₹${summary.netProfit}`} strong />
                <Divider sx={{ my: 0.75 }} />
                <PreviewRow label="Invoices Generated" value={`${summary.invoicesGenerated}`} />
                <PreviewRow label="Payments Received" value={`₹${summary.paymentsReceived}`} />
                <PreviewRow label="Outstanding Amount" value={`₹${summary.outstandingAmount}`} />

                <Box sx={{ fontSize: '0.68rem', color: MUTED, mt: 1.25, lineHeight: 1.45 }}>
                  <Typography sx={{ fontSize: 'inherit', color: 'inherit', fontWeight: 700, mb: 0.25 }}>Note:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.25 } }}>
                    <li><b>Total Sales</b> is shown including GST, so it matches the Sales screen's "Total Sales" card and the Balance Sheet.</li>
                    <li><b>Net Profit</b> is on the taxable value (GST excluded, since it is collected on the government's behalf).</li>
                    <li><b>Payments Received</b> is the actual cash collected in this period (by payment date), so it can differ from the Sales screen's "Total Received" (which counts collections against invoices dated in the period).</li>
                    <li><b>Outstanding</b> is the Sundry Debtors balance as of the period end (matches the Balance Sheet — includes opening balances).</li>
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 394, color: MUTED, fontSize: '0.8rem' }}>
                No data to preview.
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={sending} sx={{ textTransform: 'none', color: MUTED }}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || loading || !summary}
            variant="contained"
            startIcon={sending ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <WhatsAppIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 999,
              backgroundColor: '#25D366',
              '&:hover': { backgroundColor: '#1da851' }
            }}
          >
            {sending ? 'Sending…' : 'Send on WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BusinessSummarySend;
